import React from 'react';
import {
  TextField,
  Styles,
  FlatButton,
  IconButton,
  Paper,
  RaisedButton
} from 'material-ui';
import FileSelectorDialog from 'components/Widgets/FileSelectorDialog';
import DropZone from 'components/DropZone';
import { FormattedMessage } from 'react-intl';
import dropZoneStyles from 'components/Page/Module/Styles';
import CircularProgress from 'components/Material/CircularProgress';
import { FlexBox } from 'components';
import SteelCompass from 'services/SteelCompass';
import * as Icons from 'components/Material/SvgIcons';
import PageModule from 'components/PageModule';
import fullscreen from 'fullscreen';
import MediaContainer from 'components/MediaContainer';
import ResponsiveImage from 'components/ResponsiveImage';

import Message from 'services/Message';
import * as Colors from 'components/Material/Styles/Colors';
import ReactDOM from 'react-dom';

const canUseFullscreen =
  document.fullscreenEnabled ||
  document.webkitFullscreenEnabled ||
  document.msFullscreenEnabled ||
  document.mozFullScreenEnabled;

const iconColor = Colors.Colors.fullWhite;
const activeColor = Colors.Colors.grey[700].material;

const { Spacing } = Styles;

const hoverColor = Colors.Colors.grey[500].material;
const styles = {
  fullScreen: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0,
    backgroundColor: Colors.Colors.fullBlack
  },
  box: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0
  },
  root: {
    margin: 'auto',
    display: 'block',
    maxWidth: '100%',
    position: 'relative',
    paddingBottom: '56.25%' /* 16:9 */,
    height: 0
  },
  image: {
    maxWidth: '100%',
    maxHeight: '100%'
  },
  imageWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none',
    backgroundPosition: 'center center',
    backgroundSize: 'contain',
    backgroundRepeat: 'no-repeat',
    textAlign: 'center'
  },
  progressDot: {
    cursor: 'pointer'
  },
  slideshowProgress: {
    textAlign: 'center',
    minHeight: Spacing.desktopGutterMore,
    margin: Spacing.desktopGutterMini / 2
  },
  caption: {
    minHeight: Spacing.desktopGutterMore
  },
  icon: {
    filter: 'drop-shadow( 0px 0px 1px #000 )',
    WebkitFilter: 'drop-shadow( 0px 0px 1px #000 )'
  },
  verticalHelper: {
    display: 'inline-block',
    height: '100%',
    verticalAlign: 'middle'
  },
  big: {
    width: 48,
    height: 48
  },
  prevNext: {
    position: 'absolute',
    marginTop: -24,
    top: '50%',
    cursor: 'pointer'
  },
  prev: {
    left: 0
  },
  next: {
    right: 0
  },
  fullscreenControl: {
    position: 'absolute',
    right: Spacing.desktopGutterMini,
    bottom: Spacing.desktopGutterMini,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    width: Spacing.iconSize,
    height: Spacing.iconSize,
    margin: 0,
    padding: 0,
    cursor: 'pointer',
    lineHeight: `${Spacing.iconSize}px`
  },
  deleteImageButton: {
    button: {
      lineHeight: `${36}px`,
      width: 36,
      height: 36,
      minWidth: 36,
      position: 'absolute',
      right: 0,
      bottom: 0,
      verticalAlign: 'middle'
    },
    icon: {
      margin: 6
    }
  }
};

class SlideshowModule extends PageModule {
  static readable = 'SlideshowModule';

  constructor() {
    super(...arguments);
    const { content, params } = this.props;
    let caption = params.caption || [];
    const data = content || [];

    if (typeof caption === 'string') {
      caption = [caption];
    }

    const images = data.map((file, index) => {
      return { url: file.url, id: params.file_id[index] };
    });

    this.preloaded = [];

    this.state = {
      edit: {
        images,
        caption
      },
      isFullscreen: false,
      showProgress: 0,
      fileSelectorOpen: false
    };
    this.preload(0);
  }

  attach(data) {
    const { showProgress } = this.state;
    const images = this.state.edit.images.slice();
    for (let i = 0; i < data.length; i++) {
      for (let k = 0; k < images.length; k++) {
        if (data[i].file.id === images[k].id) {
          return;
        }
      }
    }
    const insertLocation =
      images.length > 0 && showProgress < images.length ? showProgress + 1 : 0;
    data.forEach(obj => {
      images.splice(insertLocation, 0, obj.file);
    });
    const edit = {
      ...this.state.edit,
      images
    };

    this.setState({ edit, showProgress: insertLocation });
    this.autoSave();
  }

  progress(event) {
    this.setState({ progress: event.percent });
  }

  preload(index) {
    const { edit } = this.state;
    const { images } = edit;
    if (!images || images.length === 0) {
      return;
    }
    const realIndex = (images.length + index) % images.length;
    if (this.preloaded[realIndex]) {
      return;
    }
    if (images[index]) {
      const image = new Image(1, 1);
      image.src = images[index].url;
      this.preloaded[index] = image;
    }
  }

  autoSave() {
    if (this._timer) {
      clearTimeout(this._timer);
    }
    this._timer = setTimeout(() => {
      Message.info(this.context.getIntlMessage('video.changesSaved'));
      this.save(false);
      this._timer = null;
    }, 1000);
  }

  changeCaption(e) {
    const caption = this.state.edit.caption.slice();
    caption[this.state.showProgress] = e.target.value;

    const edit = {
      ...this.state.edit,
      caption
    };
    this.setState({ edit });
  }

  saveOnBlur() {
    Message.info(this.context.getIntlMessage('video.changesSaved'));
    this.save(false);
  }

  fullScreen() {
    const fs = fullscreen(ReactDOM.findDOMNode(this));
    fs.on('release', () => {
      this.setState({ isFullscreen: false });
    });
    fs.on('attain', () => {
      this.setState({ isFullscreen: true });
    });
    fs.request();
  }

  previous() {
    if (this.state.showProgress > 0) {
      this.setProgress(this.state.showProgress - 1);
    }
    this.props.gaTracking();
  }

  next() {
    if (this.state.showProgress < this.state.edit.images.length - 1) {
      this.setProgress(this.state.showProgress + 1);
    }
    this.props.gaTracking();
  }

  setProgress(index) {
    this.setState({ showProgress: index });
  }

  getStyle() {
    const { mouseOver, isFullscreen } = this.state;
    const { style, mode } = this.props;
    return Object.assign(
      {},
      // styles.root,
      style,
      mode === 'edit' ? dropZoneStyles.edit : null,
      isFullscreen ? styles.fullScreen : null,
      mouseOver ? dropZoneStyles.hover : null
    );
  }

  getModuleData() {
    const { edit } = this.state;
    const { caption } = edit;
    const fileId = edit.images.map(image => image.id);
    return {
      fileId,
      caption
    };
  }

  onMediaLibrary() {
    this.setState({ fileSelectorOpen: true });
  }

  onMediaLibraryClose() {
    this.setState({ fileSelectorOpen: false });
  }

  openDownload(event) {
    this.refs.dropZone.open();
    event.stopPropagation();
    event.preventDefault();
  }

  selectMedia(file) {
    const { showProgress } = this.state;
    const images = this.state.edit.images.slice();
    const insertLocation =
      images.length > 0 && showProgress < images.length ? showProgress + 1 : 0;
    images.splice(insertLocation, 0, file);
    const edit = {
      ...this.state.edit,
      images
    };
    this.setState({
      edit,
      fileSelectorOpen: false,
      showProgress: insertLocation
    });
    this.autoSave();
  }

  import() {
    SteelCompass.call(
      'file',
      'import',
      { url: this.refs.import.value, encode: false },
      response => {
        if (!response.isError) {
          const { showProgress } = this.state;
          const images = this.state.edit.images.slice();
          const insertLocation =
            images.length > 0 && showProgress < images.length
              ? showProgress + 1
              : 0;

          images.splice(insertLocation, 0, {
            url: response.data.file.url,
            id: response.data.file.id
          });
          const edit = {
            ...this.state.edit,
            images
          };
          this.setState({ edit, showProgress: insertLocation });
          this.autoSave();
          Message.info(this.context.getIntlMessage('image.imported'));
        } else {
          Message.info(this.context.getIntlMessage('image.importFailed'));
        }
      }
    );
  }

  deleteImage() {
    let showProgress = this.state.showProgress + 0;
    const images = this.state.edit.images.slice();
    const caption = this.state.edit.caption.slice();
    images.splice(showProgress, 1);
    caption.splice(showProgress, 1);
    const edit = {
      ...this.state.edit,
      images,
      caption
    };
    if (showProgress > images.length - 1 && showProgress > 0) {
      showProgress = showProgress - 1;
    }
    this.setState({
      showProgress,
      edit
    });
    this.autoSave();
  }

  renderEditMode() {
    const { fileSelectorOpen, edit, showProgress, isFullscreen } = this.state;
    const { images } = edit;
    const url = images[showProgress] && images[showProgress].url;

    const progressDots = images.map((image, index) => {
      if (showProgress === index) {
        return (
          <Icons.ImageLens
            color={Colors.Colors.grey[500].material}
            style={styles.progressDot}
            key={index}
            onTouchTap={this.setProgress.bind(this, index)}
          />
        );
      }
      return (
        <Icons.ImagePanoramaFishEye
          color={Colors.Colors.grey[500].material}
          hoverColor={hoverColor}
          style={styles.progressDot}
          key={index}
          onTouchTap={this.setProgress.bind(this, index)}
        />
      );
    });

    this.preload(showProgress + 1);
    this.preload(showProgress - 1);

    const progressIndicator = (
      <div style={styles.slideshowProgress}>{progressDots}</div>
    );

    let previous;
    let next;
    let fullscreenControl;

    if (images.length > 0) {
      if (showProgress < images.length - 1) {
        next = (
          <div
            onClick={::this.next}
            style={Object.assign({}, styles.prevNext, styles.next)}
          >
            <Icons.NavigationChevronRight
              color={iconColor}
              hoverColor={hoverColor}
              style={Object.assign(styles.icon, styles.big)}
            />
          </div>
        );
      }
      if (showProgress > 0) {
        previous = (
          <div
            onClick={::this.previous}
            style={Object.assign({}, styles.prevNext, styles.prev)}
          >
            <Icons.NavigationChevronLeft
              color={iconColor}
              hoverColor={hoverColor}
              style={Object.assign(styles.icon, styles.big)}
            />
          </div>
        );
      }
    }

    if (!isFullscreen) {
      fullscreenControl = (
        <Paper style={styles.fullscreenControl} onTouchTap={::this.fullScreen}>
          <Icons.NavigationFullscreen
            color={activeColor}
            hoverColor={hoverColor}
          />
        </Paper>
      );
    }

    let slideshow;

    const uploadLink = (
      <a onTouchTap={::this.openDownload}>
        {this.context.getIntlMessage('module.upload')}
      </a>
    );
    const libraryLink = (
      <a onTouchTap={::this.onMediaLibrary}>
        {this.context.getIntlMessage('module.library')}
      </a>
    );
    if (this.state.progress > 0 && this.state.progress < 100) {
      slideshow = (
        <CircularProgress
          progressColor={this.context.muiTheme.rawTheme.palette.primary1Color}
          progress={this.state.progress}
          size={2}
        />
      );
    } else if (url) {
      const caption = (
        <TextField
          ref="caption"
          fullWidth
          hintText={this.context.getIntlMessage('Caption')}
          value={edit.caption[showProgress]}
          onChange={::this.changeCaption}
          onBlur={::this.saveOnBlur}
        />
      );
      slideshow = (
        <MediaContainer
          mediaObject={
            <ResponsiveImage
              style={{
                width: '100%',
                height: 300
              }}
              contain
              size={1000}
              key={url}
              src={url}
            />
          }
          controls={progressIndicator}
          caption={caption}
        >
          {previous}
          {next}
          <IconButton
            tooltip={this.context.getIntlMessage('image.delete')}
            style={styles.deleteImageButton.button}
            onTouchTap={::this.deleteImage}
          >
            <Icons.ActionDelete
              style={styles.deleteImageButton.icon}
              color={Colors.Colors.red[200].material}
              hoverColor={hoverColor}
            />
          </IconButton>
        </MediaContainer>
      );
    } else {
      slideshow = <Icons.ImageImage style={dropZoneStyles.icon} />;
    }

    return (
      <div>
        <DropZone
          accept="image/jpeg,image/jpg,image/gif,image/png"
          ref="dropZone"
          style={dropZoneStyles.dropZone}
          lineHeight={Spacing.desktopKeylineIncrement / 2}
          onProgress={::this.progress}
          onSuccess={::this.attach}
        >
          <div style={dropZoneStyles.moduleContainer}>{slideshow}</div>
          <div style={dropZoneStyles.header}>
            {this.context.getIntlMessage('module.dropImages')}
          </div>
          <div style={dropZoneStyles.subHeader}>
            <FormattedMessage
              message={this.context.getIntlMessage('module.uploadOrBrowse')}
              upload={uploadLink}
              library={libraryLink}
            />
          </div>
          <div style={dropZoneStyles.subHeader}>
            {this.context.getIntlMessage('module.youCanImport')}
          </div>

          <Paper style={dropZoneStyles.import.root}>
            <FlexBox>
              <FlexBox>
                <input
                  ref="import"
                  style={dropZoneStyles.import.input}
                  type="text"
                  placeholder={this.context.getIntlMessage('importFromUrl')}
                />
              </FlexBox>
              <FlexBox grow={0}>
                <FlatButton
                  secondary
                  label={this.context.getIntlMessage('Import')}
                  onTouchTap={::this.import}
                />
              </FlexBox>
            </FlexBox>
          </Paper>
        </DropZone>
        <FileSelectorDialog
          type="image"
          open={fileSelectorOpen}
          onSelect={::this.selectMedia}
          onClose={::this.onMediaLibraryClose}
        />
      </div>
    );
  }

  renderDetailMode() {
    const { edit, showProgress, isFullscreen } = this.state;
    const { images } = edit;
    let { caption } = edit;
    const url = images[showProgress] && images[showProgress].url;

    const progressDots = images.map((image, index) => {
      if (showProgress === index) {
        return (
          <Icons.ImageLens
            color={Colors.Colors.grey[500].material}
            style={styles.progressDot}
            key={index}
            onTouchTap={this.setProgress.bind(this, index)}
          />
        );
      }
      return (
        <Icons.ImagePanoramaFishEye
          color={Colors.Colors.grey[500].material}
          hoverColor={hoverColor}
          style={styles.progressDot}
          key={index}
          onTouchTap={this.setProgress.bind(this, index)}
        />
      );
    });

    this.preload(showProgress + 1);
    this.preload(showProgress - 1);

    const progressIndicator = (
      <div style={styles.slideshowProgress}>{progressDots}</div>
    );

    let previous;
    let next;
    let fullscreenControl;

    if (images.length > 0) {
      if (showProgress < images.length - 1) {
        next = (
          <div
            onClick={::this.next}
            style={Object.assign({}, styles.prevNext, styles.next)}
          >
            <Icons.NavigationChevronRight
              color={iconColor}
              hoverColor={hoverColor}
              style={Object.assign(styles.icon, styles.big)}
            />
          </div>
        );
      }
      if (showProgress > 0) {
        previous = (
          <div
            onClick={::this.previous}
            style={Object.assign({}, styles.prevNext, styles.prev)}
          >
            <Icons.NavigationChevronLeft
              color={iconColor}
              hoverColor={hoverColor}
              style={Object.assign(styles.icon, styles.big)}
            />
          </div>
        );
      }
    }

    if (canUseFullscreen && !isFullscreen) {
      fullscreenControl = (
        <Paper style={styles.fullscreenControl} onTouchTap={::this.fullScreen}>
          <Icons.NavigationFullscreen
            color={activeColor}
            hoverColor={hoverColor}
          />
        </Paper>
      );
    }

    if (url) {
      return (
        <MediaContainer
          mediaObject={
            <ResponsiveImage
              style={{
                width: '100%',
                height: 300
              }}
              contain
              size={1000}
              key={url}
              src={url}
            />
          }
          controls={progressIndicator}
          caption={caption[showProgress]}
        >
          {previous}
          {next}
          {fullscreenControl}
        </MediaContainer>
      );
    }
    return (
      <div style={dropZoneStyles.dropZone} onTouchTap={::this.changeToEditMode}>
        <div style={dropZoneStyles.moduleContainer}>
          <Icons.ImageSlideshow style={dropZoneStyles.icon} />
          <br />
          {this.context.getIntlMessage('module.uploadImage')}
        </div>
      </div>
    );
  }
}

export default SlideshowModule;



// WEBPACK FOOTER //
// ./src/js/components/Page/Module/Slideshow.js